import React, { useCallback, useEffect } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { AxiosRequestConfig } from "axios"
import { jwtDecode } from "jwt-decode"
import { useMMKVString } from "react-native-mmkv"
import { getLoginRole } from "types/role"

import type { SponsorInfo } from "src/services/api/api.types"
import { SessionTakeoverDialog } from "src/components/SessionTakeoverDialog"
import { SponsorInactiveDialog } from "src/components/SponsorInactiveDialog"
import Config, { getEnvExtraVar } from "src/config"
import { AUTH_HOSTS } from "src/config/config.base"
import { clearLastShownSplashSponsorId } from "src/context/channelSplashMemory"
import { useClaimSessionOnForeground } from "src/hooks/useClaimSessionOnForeground"
import { clearVideoThumbnailCache } from "src/hooks/useVideoThumbnail"
import { useClearApiCache } from "src/queries/useApi"
import { useClearConfigCache } from "src/queries/useConfig"
import {
  useClearCachedConversations,
  useClearConversationStatusFlags,
} from "src/queries/useConversation"
import {
  getUserSelectedSession,
  setUserSelectedSession,
  useClearRoleDataAndSelectedSession,
} from "src/queries/useRole"
import { getLogoutStatus, useClearUserCache, useLogoutStatus } from "src/queries/useUser"
import { Api, AuthApi, AuthTokens } from "src/services/api"
import { GeneralApiProblem } from "src/services/api/apiProblem"
import { clearContextToken, getContextToken } from "src/utils/contextToken"
import { getSafeRequestInfo, logSentryBreadcrumb, logSentryEvent } from "src/utils/crashReporting"
import { clear as clearAllMmkvStorage } from "src/utils/storage"
import { sessionScopedStorage, storage } from "src/utils/storage/mmkv"
import { parseUrl } from "src/utils/urlUtils"

const AUTH_TOKENS_STORAGE_KEY = "authTokens"

// Special app review account that always routes to staging auth server
const APP_REVIEW_EMAIL = "apptest@sidekickwellness.com"
const APP_REVIEW_AUTH_HOST = AUTH_HOSTS.STAG

interface AuthContextType {
  isRefreshing: boolean
  isAuthenticated: boolean
  initialAuthStateLoaded: boolean
  signIn: (args: {
    email: string
    password: string
  }) => Promise<
    { kind: "ok"; requiresSponsorSelection: boolean } | { kind: "error"; message: string }
  >
  changePassword: (args: {
    email: string
    oldPassword: string
    newPassword: string
  }) => Promise<{ kind: "ok" } | { kind: "error"; message: string }>
  clearApiData: () => void
  clearApiCache: () => void
  clearSponsorData: () => void
  clearAllUserData: () => void
  clearAllTemporaryData: () => void
  clearAllStorageData: () => Promise<void>

  logout: () => Promise<void>
  needsRefresh: () => boolean
  getAuthToken: () => Promise<string | undefined>
  getUserEmail: () => string | undefined

  api: Api
  authApi: AuthApi
  apiHost: string
  setApiHost: (host: string) => void
  updateApiHostFromBackend: (
    apiBaseUrl: string,
    options?: { alsoUpdateDefault?: boolean; skipPersist?: boolean },
  ) => { kind: "ok" } | { kind: "error"; message: string }
  authHost: string
  setAuthHost: (host: string) => void
  sponsorId: string
  switchSponsor: (
    sponsorId: string,
    opts?: { isLogin?: boolean; role?: import("types/role").UserRole },
  ) => Promise<void>

  /**
   * Sponsor list sourced from the /login response, held in-memory for the post-login
   * /select-sponsor screen. Cleared once the user picks a sponsor or on logout.
   * If null, SelectSponsor falls back to useSponsorListQuery().
   */
  pendingLoginSponsors: SponsorInfo[] | null
  setPendingLoginSponsors: (sponsors: SponsorInfo[] | null) => void

  /** Exclusive session: displaced by another login for same user/role context */
  isSessionDeactivated: boolean
  /**
   * Terminal: the active sponsor/channel was deleted out from under the user
   * (backend 401 INVALID_SPONSOR_CONTEXT / SPONSOR_NOT_ACTIVE_IN_ENVIRONMENT).
   * Non-recoverable — there is no session to resume. Surfaces the
   * SponsorInactiveDialog ("Choose another channel" / "Log out") instead of the
   * takeover dialog. See sessionErrors.ts for the classification precedence.
   */
  isSponsorInactive: boolean
  /**
   * "Choose another channel" path for the terminal sponsor-inactive state:
   * clears this tab's context token, sponsor selection, and per-channel caches
   * (keeping Cognito auth) so the app routes to the channel-selection screen.
   */
  recoverFromSponsorInactive: () => void
  /**
   * True while POST /context/switch (login bootstrap) is in-flight. SESSION_DEACTIVATED
   * responses are suppressed during this window to prevent a coincident 401 from a
   * parallel in-flight request from showing the takeover dialog mid-bootstrap.
   */
  isRegistrationPending: boolean
  /**
   * True for the entire duration of signIn() — from after a successful login
   * response until claimSession + getRoleInfo + reportActivity have all settled.
   *
   * AuthExclusiveSessionEffects combines this with isRegistrationPending to
   * suppress the foreground hook's bootstrap claim (which would otherwise mint
   * a competing session_id during signIn) and to suppress the
   * SESSION_DEACTIVATED takeover dialog so the warning does not
   * appear before the user has settled into a channel.
   */
  isSigningIn: boolean
  claimSession: () => Promise<{ kind: "ok" } | { kind: "error"; message: string }>
  /**
   * Lightweight session refresh: POST /context/refresh (if a token is stored).
   * Use this on foreground — does NOT reset role/sponsor, does not bootstrap a new session.
   */
  notifySession: () => Promise<{ kind: "ok" } | { kind: "error"; message: string }>
  resumeSession: () => Promise<{ kind: "ok" } | { kind: "error"; message: string }>
  markSessionDeactivated: () => void
  /** Clear local state only; skips server POST /logout (session already inactive). */
  localLogoutAfterDeactivation: () => void
}

const AuthContext = React.createContext<AuthContextType | null>(null)

function toBaseURL(host: string): string {
  const hostname = host.split(":")[0]
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1"
  const scheme = isLocal ? "http" : "https"
  return `${scheme}://${host}`
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient()

  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [authTokens, setAuthTokens] = React.useState<AuthTokens | undefined>()
  const [initialAuthStateLoaded, setInitialAuthStateLoaded] = React.useState(false)
  const [pendingLoginSponsors, setPendingLoginSponsors] = React.useState<SponsorInfo[] | null>(null)
  // Ref mirror so stable Api callbacks can check whether we are still
  // waiting for the user to pick a sponsor (no context token exists yet).
  const pendingLoginSponsorsRef = React.useRef<SponsorInfo[] | null>(null)
  const [isSessionDeactivated, setIsSessionDeactivated] = React.useState(false)
  // Ref mirror of isSessionDeactivated. Required because the api recovery
  // callbacks (onSessionDeactivated, onContextTokenError) are captured by the
  // Api instance once at construction and never re-bind to fresh state — so
  // reading `isSessionDeactivated` directly inside them would always observe
  // the initial value (false). The ref gives them the latest value via the
  // useEffect mirror below. This is the same pattern as
  // isRegistrationPendingRef and isSigningInRef.
  const isSessionDeactivatedRef = React.useRef(false)
  // Terminal sponsor-gone state. Like isSessionDeactivated this needs a ref
  // mirror because the Api recovery callbacks are captured once at construction
  // and never re-bind to fresh state.
  const [isSponsorInactive, setIsSponsorInactive] = React.useState(false)
  const isSponsorInactiveRef = React.useRef(false)
  const [isRegistrationPending, setIsRegistrationPending] = React.useState(false)
  // Ref mirror of isRegistrationPending for use inside stable closures (Api callbacks,
  // useCallbacks with stale captures) where React state would not be current.
  const isRegistrationPendingRef = React.useRef(false)
  // Race guard: covers the entire signIn flow (login → claimSession →
  // getRoleInfo → reportActivity). Set synchronously BEFORE updateAuthTokens so
  // the foreground claim hook sees it on the same render that flips
  // isAuthenticated=true and skips its redundant bootstrap claim.
  const [isSigningIn, setIsSigningIn] = React.useState(false)
  const isSigningInRef = React.useRef(false)
  // Holds the in-flight notifyLoginAndVerify promise so concurrent HTTP
  // requests can wait for bootstrap completion instead of erroring out with
  // CONTEXT_TOKEN_MISSING / SESSION_DEACTIVATED. Any recovery callback that
  // fires while bootstrap is in progress awaits this promise, then signals
  // "retry" if the bootstrap successfully populated the context token.
  // Cleared in notifyLoginAndVerify's finally block.
  const bootstrapPromiseRef = React.useRef<Promise<unknown> | null>(null)

  // Ref for immediate access to auth tokens (bypasses React's async state updates)
  const authTokensRef = React.useRef<AuthTokens | undefined>(authTokens)
  // authHost is static per environment (the build-time auth server endpoint —
  // same for all sponsors / users on a given build), so it stays in the
  // shared per-user MMKV.
  const [persistedAuthHost, setAuthHost] = useMMKVString("authHost")
  // apiHost is returned by the auth host per-sponsor (different sponsors
  // may be hosted on different envs — dev1 vs dev2 vs staging), so it MUST
  // follow sponsorId per-tab. Otherwise Tab A's switch to a Sponsor X on
  // dev2 would clobber Tab B's apiHost (in Sponsor Y on dev1) and Tab B's
  // next request would go to the wrong backend. Backed by sessionStorage
  // on web via MMKV's `webStorage: "session"` patch; native still has a
  // single tab so the namespace is just a separate MMKV instance.
  const [persistedApiHost, setApiHost] = useMMKVString("apiHost", sessionScopedStorage)
  // defaultApiHost is the apiHost the auth host returned for the user's
  // default sponsor at the most recent login. It lives in the per-user
  // (localStorage) MMKV so a freshly-opened browser tab — which inherits
  // Cognito tokens from localStorage but starts with an empty sessionStorage
  // (no per-tab apiHost) — has a valid host to call `getSponsorList()`
  // against. Once the user picks a sponsor in that tab, the per-tab `apiHost`
  // (sessionStorage) takes precedence and `defaultApiHost` is left untouched
  // (it stays a per-user "default" the next new tab can fall back to).
  // Cleared on logout.
  const [persistedDefaultApiHost, setDefaultApiHost] = useMMKVString("defaultApiHost")
  // Legacy migration fallback. Earlier installations stored `apiHost` in
  // the default (localStorage-backed) MMKV instance. After per-tab `apiHost`
  // moved to `sessionScopedStorage` (sessionStorage on web), users who were
  // already-authenticated when they upgraded had Cognito tokens valid in
  // localStorage but no per-tab sessionStorage `apiHost` and no
  // `defaultApiHost` (signIn never ran). The result was
  // `apiHost === "unknown-api-host"` on first reload, breaking
  // `getSponsorList()` and crashing `/select-sponsor`. We read the legacy
  // localStorage value here as a final fallback so upgrade-without-relogin
  // works. New users / fresh logins never hit this path because signIn
  // writes `defaultApiHost` (which wins over this fallback in the
  // resolution chain). Read-only — we never write to this slot.
  const [legacyLocalStorageApiHost] = useMMKVString("apiHost", storage)
  const [persistedSponsorId, setSponsorId] = useMMKVString("sponsorId", sessionScopedStorage)
  const { isLoggingOut, setIsLoggingOut } = useLogoutStatus()
  const clearConfigCache = useClearConfigCache()
  const clearUserCache = useClearUserCache()
  const clearCachedConversations = useClearCachedConversations()
  const clearConversationStatusFlags = useClearConversationStatusFlags()
  const clearApiCache = useClearApiCache()
  const clearRoleData = useClearRoleDataAndSelectedSession()
  const localApiHost = React.useMemo(() => getEnvExtraVar("LOCAL_API_HOST"), [])
  const localAuthHost = React.useMemo(() => getEnvExtraVar("LOCAL_AUTH_HOST"), [])
  const defaultAuthHost = React.useMemo(() => Config.DEFAULT_AUTH_HOST, [])

  // apiHost resolution order:
  //   1. localApiHost (build-time override, e.g. dev simulator pointing at a
  //      local backend)
  //   2. persistedApiHost — the per-tab (sessionStorage) host. This is what
  //      the user actively selected/switched to in *this* tab. Once set, this
  //      tab stays pinned to it across reloads.
  //   3. persistedDefaultApiHost — the per-user (localStorage) fallback set
  //      during signIn from the auth host's response. Used by a freshly-opened
  //      tab that inherits the user's Cognito tokens from localStorage but has
  //      an empty sessionStorage (so no per-tab apiHost yet). Lets the new tab
  //      call `getSponsorList()` and reach `/select-sponsor` instead of
  //      hanging against `"unknown-api-host"`. The instant the user picks a
  //      sponsor, switchSponsor writes the chosen apiHost to sessionStorage
  //      (step 2 above) and we never read this fallback again for this tab.
  //   4. legacyLocalStorageApiHost — migration fallback for users who were
  //      already authenticated when they upgraded to per-tab apiHost. After
  //      re-authentication signIn populates `defaultApiHost` and this slot
  //      is no longer consulted (step 3 wins).
  //   5. "unknown-api-host" — last-ditch placeholder; downstream code that
  //      reaches this state is by definition unauthenticated.
  const apiHost =
    localApiHost ||
    persistedApiHost ||
    persistedDefaultApiHost ||
    legacyLocalStorageApiHost ||
    "unknown-api-host"
  const authHost = localAuthHost || persistedAuthHost || defaultAuthHost
  const sponsorId = persistedSponsorId || "unknown-sponsor"

  const minutesUntilExpiration = (tokens: AuthTokens) => {
    // ExpiresAt is in the user's current timezone, not GMT
    return (Number(tokens.ExpiresAt) - Date.now()) / 1000 / 60
  }

  const needsRefresh = React.useCallback(() => {
    // Use ref for immediate access to fresh tokens (consistent with getAuthToken)
    const currentTokens = authTokensRef.current
    if (!currentTokens?.ExpiresAt) return false

    // This check represents how long of an overlap we'd like to support
    // (but aren't guaranteeing) between the time we attempt refreshing the token
    // and the time the server actually processes that refresh.
    // If a request is very close to expiring, but is processed the server as expired,
    // we'd receive an authentication failure which we must interpret as a logout.
    // Instead we give an amount of time in the future we're willing to lose from the token
    // that should cover the worst case processing time.
    // If it's clearly expired alread the choice is very easy, and is also covered by this check.
    return minutesUntilExpiration(currentTokens) < 2
  }, [authTokensRef])

  const refreshPromise = React.useRef<Promise<AuthTokens | undefined> | undefined>(undefined)

  // Helper function to update both state and ref simultaneously
  const updateAuthTokens = React.useCallback(
    (tokens: AuthTokens | undefined) => {
      console.log("[AUTH_CONTEXT] Updating auth tokens:", tokens ? "SET" : "CLEARED")
      authTokensRef.current = tokens
      setAuthTokens(tokens)
    },
    [setAuthTokens, authTokensRef],
  )

  useEffect(() => {
    const storedTokens = storage.getString(AUTH_TOKENS_STORAGE_KEY)
    if (storedTokens) {
      console.log("Restoring auth tokens from storage")
      const tokens = JSON.parse(storedTokens)
      updateAuthTokens(tokens)
    }
    setInitialAuthStateLoaded(true)
    // FIXME: maybe a good spot for the new useEffectEvent so we don't need this lint directive?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (authTokens) {
      // TODO:
      // right now we're storing the entire authTokens object - both access and refresh tokens in MMKV storage
      // we should arguably be using the Keystore instead for more secure storage
      console.log("[AUTH_CONTEXT] Storing auth tokens to storage")
      storage.set(AUTH_TOKENS_STORAGE_KEY, JSON.stringify(authTokens))
    } else {
      if (initialAuthStateLoaded) {
        console.log("[AUTH_CONTEXT] Removing auth tokens from storage")
        storage.delete(AUTH_TOKENS_STORAGE_KEY)
      }
    }
  }, [authTokens, initialAuthStateLoaded])

  // Create AuthApi instance
  const authApi = React.useMemo(() => {
    return new AuthApi({
      config: {
        url: toBaseURL(authHost),
        timeout: 10000,
      },
    })
  }, [authHost])

  const getAuthToken = useCallback(async (): Promise<string | undefined> => {
    // Use ref for immediate access to fresh tokens
    const currentTokens = authTokensRef.current

    if (!currentTokens?.IdToken || !currentTokens?.RefreshToken) {
      console.log("[AUTH_CONTEXT] No tokens available for auth header")
      return undefined
    }

    if (needsRefresh()) {
      console.log("[AUTH_CONTEXT] Token needs refresh, refreshing...")
      logSentryBreadcrumb("token_refresh_attempt")
      if (!refreshPromise.current) {
        setIsRefreshing(true)
        refreshPromise.current = authApi
          .refresh(currentTokens.RefreshToken)
          .then((response) => {
            if (response.kind === "ok") {
              console.log("[AUTH_CONTEXT] Token refresh successful")
              logSentryBreadcrumb("token_refresh_success")
              updateAuthTokens(response.result)
              return response.result
            } else {
              if (response.kind === "unauthorized" || response.kind === "forbidden") {
                // Handle stale refresh tokens. These won't be accepted for refresh due to expiration.
                console.log("[AUTH_CONTEXT] Refresh token is invalid. Clearing tokens.")
              } else {
                // Handle other API problems that are not related to the refresh token expiration.
                // We've reached maximum retries without success.
                console.warn(
                  "[AUTH_CONTEXT] Token refresh failed due to either bad client request, or network/server error. Clearing tokens.",
                  response.kind,
                )
              }

              // Log token refresh failure and auto-logout
              logSentryEvent("warning", "auto_logout_token_refresh_failed", {
                reason: response.kind,
              })

              // Clear tokens regardless of the error type
              updateAuthTokens(undefined)
              return undefined
            }
          })
          .finally(() => {
            refreshPromise.current = undefined
            setIsRefreshing(false)
          })
      }

      const refreshTokens = await refreshPromise.current
      if (refreshTokens) {
        return refreshTokens.IdToken
      }
    } else {
      console.log(
        "[AUTH_CONTEXT] Token is still valid for",
        minutesUntilExpiration(currentTokens),
        "minutes",
      )
      return currentTokens.IdToken
    }

    return undefined
  }, [authApi, needsRefresh, updateAuthTokens])

  const handleAuthFailure = useCallback(
    (
      eventName: "auto_logout_unauthorized" | "auto_logout_forbidden",
      logMessage: string,
      requestConfig: AxiosRequestConfig,
      response: { status?: number },
    ) => {
      // Suppress auto-logout when a backend version mismatch is already known.
      // The outdated screen is showing; clearing tokens would kick the user back
      // to login before they can see the "Update Required" prompt.
      const cachedVersion = queryClient.getQueryData<string>(["backendAPIVersion"])
      if (cachedVersion !== undefined && cachedVersion !== Config.BACKEND_API_VERSION) {
        console.log(
          "[AUTH_CONTEXT] Suppressing auto-logout: version mismatch detected",
          cachedVersion,
        )
        return
      }
      console.log(logMessage)
      // Avoid logging most request and response data as it may contain sensitive information
      const safeRequestInfo = getSafeRequestInfo(requestConfig)
      logSentryEvent("error", eventName, {
        ...safeRequestInfo,
        status: response.status,
      })
      updateAuthTokens(undefined)
    },
    [updateAuthTokens, queryClient],
  )

  // Create Api instance
  const api = React.useMemo(() => {
    const apiInstance = new Api({
      config: {
        url: toBaseURL(apiHost),
        timeout: 10000,
      },
      onUnauthorized: (requestConfig, response) => {
        handleAuthFailure(
          "auto_logout_unauthorized",
          "Unauthorized API response, clearing auth tokens",
          requestConfig,
          response,
        )
      },
      onForbidden: (requestConfig, response) => {
        handleAuthFailure(
          "auto_logout_forbidden",
          "Forbidden API response, clearing auth tokens",
          requestConfig,
          response,
        )
      },
      onSessionDeactivated: async (requestConfig, response) => {
        // Returns true if the original request should be retried by api.ts
        // (only applies during the bootstrap-in-flight race below); false
        // otherwise → caller observes the 401 and the takeover dialog is
        // surfaced here.
        //
        // SESSION_DEACTIVATED means the backend has displaced this tab's
        // session_id (another tab/device opened a new session). We do NOT
        // attempt silent POST /context/refresh here. Reason: when Tab 2
        // displaces Tab 1, calling refresh from Tab 1 with its stale token
        // causes the backend to remint a new token bound to the CURRENT
        // active session (Tab 2's), so both tabs end up sharing the same
        // session_id — defeating the entire exclusive-session model. The
        // correct response is to surface the takeover dialog so the user
        // can choose "Resume Here" (which then explicitly calls
        // reactivateContext from `resumeSession`).

        // Terminal sponsor-gone state wins: if the channel was deleted we are
        // already routing the user out — never surface the takeover dialog.
        if (isSponsorInactiveRef.current) {
          return false
        }

        // Frozen-while-deactivated guard: if the dialog is already up, this
        // is a follow-up 401 from a query that fired before cancelQueries
        // settled. setIsSessionDeactivated would just be a no-op, but logging
        // a duplicate sentry event for every refetch-on-focus 401 adds noise.
        // Bail silently.
        if (isSessionDeactivatedRef.current) {
          return false
        }

        // Awaiting-bootstrap guard: any 401 received while no context token is
        // stored in this tab is necessarily noise — the request was always going
        // to fail because we have nothing to identify the session with. Covers:
        //   * multi-sponsor login (token cleared in signIn → user on select-sponsor)
        //   * fresh tab inheriting Cognito tokens but no sessionStorage
        //   * post-logout login before bootstrap
        // Letting these surface the takeover dialog is misleading — there is no
        // session to be displaced. The user will land on the select-sponsor /
        // bootstrap path momentarily.
        if (pendingLoginSponsorsRef.current !== null || !getContextToken()) {
          console.warn(
            "[AUTH_CONTEXT] SESSION_DEACTIVATED suppressed — no context token (awaiting bootstrap)",
            getSafeRequestInfo(requestConfig),
          )
          return false
        }

        // Bootstrap-in-flight guard: if the login bootstrap is already running,
        // wait for it instead of double-firing recovery (which would race with
        // the bootstrap's own context_token rotation). After bootstrap completes
        // successfully, the request will be replayed against the freshly
        // stored token by the api.ts auto-retry path. This addresses the race
        // observed when useCurrentConversationQuery (and friends) fire the
        // moment the app mounts, before the login bootstrap has stored a token.
        if (bootstrapPromiseRef.current) {
          console.log(
            "[AUTH_CONTEXT] SESSION_DEACTIVATED — awaiting in-flight bootstrap",
            getSafeRequestInfo(requestConfig),
          )
          await bootstrapPromiseRef.current.catch(() => undefined)
          return getContextToken() !== null
        }

        // Defensive: signIn sets isSigningInRef before notifyLogin starts. If a
        // request fires in that brief window (no bootstrap promise yet, but
        // signIn is in flight), we still suppress to avoid showing the
        // takeover dialog before the user has settled.
        if (isRegistrationPendingRef.current || isSigningInRef.current) {
          console.warn(
            "[AUTH_CONTEXT] SESSION_DEACTIVATED suppressed during bootstrap (no promise to await)",
            getSafeRequestInfo(requestConfig),
          )
          return false
        }

        // Real displacement — surface the takeover dialog. The user can
        // choose "Resume Here" (resumeSession → replaceContextToken
        // fallback to notifyLogin) or "Log out". No silent recovery here,
        // by design.
        logSentryEvent("warning", "session_deactivated_http", {
          ...getSafeRequestInfo(requestConfig),
          status: response.status,
        })
        // Set the ref synchronously alongside the state update so any
        // recovery callback that fires before the next React render sees
        // the up-to-date value (the useEffect mirror runs only after
        // re-render). Without this, two parallel 401s landing in the same
        // task could both pass the ref guard above and double-trigger.
        isSessionDeactivatedRef.current = true
        setIsSessionDeactivated(true)
        return false
      },
      onSponsorInactive: async (requestConfig, response) => {
        // Terminal: the sponsor/channel was deleted (INVALID_SPONSOR_CONTEXT /
        // SPONSOR_NOT_ACTIVE_IN_ENVIRONMENT). There is no session to resume and
        // no token that will ever validate. Surface the SponsorInactiveDialog so
        // the user can pick another channel or log out — NOT the takeover
        // dialog, and never a silent reactivate/refresh (which would loop).
        //
        // CRITICAL: the backend also returns this 401 for a STALE context token
        // — e.g. an in-flight request from a previous session / sponsor / env
        // that resolves after a re-login or sponsor switch. The token simply
        // isn't valid in the target environment anymore; the channel itself is
        // fine. We must NOT treat those races as terminal (they manifested as a
        // spurious "Channel no longer available" right after a clean re-login on
        // Android). The guards below restrict the terminal verdict to the one
        // unambiguous case: a request carrying THIS tab's current, live context
        // token was rejected as sponsor-inactive.

        // Idempotent: parallel in-flight requests may each land here.
        if (isSponsorInactiveRef.current) {
          return
        }

        // Suppress during sign-in / bootstrap: a deleted sponsor on the login
        // path surfaces its own error from switchContext(isLogin:true) and is
        // handled by the login flow, not this terminal dialog.
        if (isRegistrationPendingRef.current || isSigningInRef.current) {
          console.warn(
            "[AUTH_CONTEXT] sponsor-inactive suppressed during bootstrap",
            getSafeRequestInfo(requestConfig),
          )
          return
        }

        // Awaiting-bootstrap guard: no stored token (or still on select-sponsor)
        // means the failing request leaked out before a context was established.
        if (pendingLoginSponsorsRef.current !== null || !getContextToken()) {
          console.warn(
            "[AUTH_CONTEXT] sponsor-inactive suppressed — no context token (awaiting bootstrap)",
            getSafeRequestInfo(requestConfig),
          )
          return
        }

        // If a login bootstrap is still settling, wait for it before judging.
        if (bootstrapPromiseRef.current) {
          await bootstrapPromiseRef.current.catch(() => undefined)
          if (!getContextToken()) {
            return
          }
        }

        // Stale-token guard (the Android relaunch / re-login race). Only go
        // terminal when the failing request was sent with the SAME context token
        // that is currently stored. A missing or mismatched token means the
        // request predates the current context (a previous session / sponsor /
        // environment) — the sponsor is "not active" only for that stale token,
        // not the live one. Live queries with the fresh token will refetch fine,
        // so we just bail here.
        const headers = (requestConfig.headers ?? {}) as Record<string, unknown>
        const sentToken =
          (headers["X-Context-Token"] as string | undefined) ??
          (headers["x-context-token"] as string | undefined)
        const currentToken = getContextToken()
        if (!sentToken || !currentToken || sentToken !== currentToken) {
          console.warn(
            "[AUTH_CONTEXT] sponsor-inactive suppressed — failing request did not carry the current context token (stale pre-login/switch race)",
            getSafeRequestInfo(requestConfig),
          )
          return
        }

        console.warn(
          "[AUTH_CONTEXT] sponsor inactive (channel deleted) — surfacing terminal dialog",
          getSafeRequestInfo(requestConfig),
        )
        logSentryEvent("warning", "sponsor_inactive_http", {
          ...getSafeRequestInfo(requestConfig),
          status: response.status,
          code: (response.data as { code?: string; reason?: string } | undefined)?.code,
          reason: (response.data as { code?: string; reason?: string } | undefined)?.reason,
        })
        // Set the ref synchronously so coincident callbacks observe it before
        // the next render. Clear any takeover dialog a racing WS 4001 close may
        // have surfaced — the terminal dialog supersedes it.
        isSponsorInactiveRef.current = true
        setIsSponsorInactive(true)
        isSessionDeactivatedRef.current = false
        setIsSessionDeactivated(false)
      },
      onContextTokenError: async (requestConfig, response) => {
        // Returns true on successful recovery so api.ts retries the original
        // request transparently; returns false otherwise (the caller observes
        // the 401 — no dialog, since CONTEXT_TOKEN_* is not displacement).
        //
        // Recovery contract (unified endpoints):
        //   * has stored token → POST /context/refresh ONLY. No fallback to any
        //     bootstrap endpoint (login bootstrap requires a sponsor_id that we
        //     cannot derive here without user interaction).
        //   * no stored token → cannot silently bootstrap; escalate directly to
        //     the session-deactivated dialog so the user can choose Resume Here.
        //
        // A 401 from /context/refresh escalates to the deactivated dialog — the
        // backend has definitively rejected our ability to remint the token.

        if (isSponsorInactiveRef.current) {
          console.log(
            "[AUTH_CONTEXT] CONTEXT_TOKEN error suppressed — sponsor inactive (terminal)",
            getSafeRequestInfo(requestConfig),
          )
          return false
        }

        if (isSessionDeactivatedRef.current) {
          console.log(
            "[AUTH_CONTEXT] CONTEXT_TOKEN error suppressed — session already deactivated",
            getSafeRequestInfo(requestConfig),
          )
          return false
        }

        // Same awaiting-bootstrap guard as onSessionDeactivated: a CONTEXT_TOKEN_*
        // error on a request that had no token to send means the request leaked
        // out before bootstrap (multi-sponsor login, fresh tab, etc.). There is
        // nothing to refresh — the next user action (sponsor selection) will mint
        // the token via switchContext.
        if (pendingLoginSponsorsRef.current !== null || !getContextToken()) {
          console.warn(
            "[AUTH_CONTEXT] CONTEXT_TOKEN error suppressed — no context token (awaiting bootstrap)",
            getSafeRequestInfo(requestConfig),
          )
          return false
        }

        if (bootstrapPromiseRef.current) {
          console.log(
            "[AUTH_CONTEXT] CONTEXT_TOKEN error — awaiting in-flight bootstrap",
            getSafeRequestInfo(requestConfig),
          )
          await bootstrapPromiseRef.current.catch(() => undefined)
          return getContextToken() !== null
        }

        if (isRegistrationPendingRef.current || isSigningInRef.current) {
          console.warn(
            "[AUTH_CONTEXT] CONTEXT_TOKEN error suppressed during bootstrap (no promise to await)",
            getSafeRequestInfo(requestConfig),
          )
          return false
        }

        const code = (response.data as { code?: string } | undefined)?.code
        logSentryBreadcrumb(`context_token_error_recovery_start code=${code ?? "(none)"}`)

        if (!getContextToken()) {
          // No stored token — cannot silently bootstrap (requires sponsor_id).
          // Escalate directly to the deactivated dialog.
          logSentryEvent("warning", "context_token_missing_escalating_to_dialog", {
            ...getSafeRequestInfo(requestConfig),
            status: response.status,
            code,
          })
          isSessionDeactivatedRef.current = true
          setIsSessionDeactivated(true)
          return false
        }

        try {
          const refreshResult = await apiInstance.refreshContext()
          if (refreshResult.kind === "ok") {
            logSentryBreadcrumb(`context_token_error_refresh_success code=${code ?? "(none)"}`)
            return true
          }
          console.warn("[AUTH_CONTEXT] CONTEXT_TOKEN refresh failed:", refreshResult.kind)
          if (refreshResult.kind === "unauthorized") {
            const refreshCode = (refreshResult.data as { code?: string } | undefined)?.code
            logSentryEvent("warning", "context_token_refresh_unauthorized_escalating", {
              ...getSafeRequestInfo(requestConfig),
              original_code: code,
              refresh_code: refreshCode,
            })
            isSessionDeactivatedRef.current = true
            setIsSessionDeactivated(true)
            return false
          }
        } catch (e) {
          console.warn("[AUTH_CONTEXT] CONTEXT_TOKEN refresh threw:", e)
        }

        logSentryEvent("warning", "context_token_recovery_failed", {
          ...getSafeRequestInfo(requestConfig),
          status: response.status,
          code,
        })
        return false
      },
      getAuthToken,
    })
    return apiInstance
  }, [apiHost, getAuthToken, handleAuthFailure])

  // Either use the local override, parse and use the given URL, or use the persisted value
  const resolveApiUrl = useCallback(
    (
      apiBaseUrl: string,
    ): { kind: "ok"; url: URL; usedOverride: boolean } | { kind: "error"; message: string } => {
      // Resolve the API host to use going forward, honoring LOCAL_API_HOST override.
      let resolvedApiUrl: URL | undefined
      let usedOverride: boolean = false

      // Use the local override if it exists
      if (localApiHost) {
        resolvedApiUrl = new URL(localApiHost)
        usedOverride = true
      } else {
        // Otherwise, parse the API base URL
        if (apiBaseUrl) {
          const parseResult = parseUrl(apiBaseUrl)
          if (parseResult.kind === "ok") {
            resolvedApiUrl = parseResult.result
          } else {
            console.error(
              "[AUTH_CONTEXT] Invalid apiBaseUrl:",
              parseResult.message,
              parseResult.error,
            )
            return { kind: "error", message: "Invalid server API host" }
          }
        } else if (persistedApiHost) {
          // Otherwise, use the persisted API host
          resolvedApiUrl = new URL(persistedApiHost)
        }
      }

      // If we still don't have a host, fail login
      if (!resolvedApiUrl) {
        return { kind: "error", message: "No API host available" }
      }

      // Validate host again by attempting to construct a URL
      try {
        // This also validates and detects the schema for localhost and standard domains
        resolvedApiUrl = new URL(toBaseURL(resolvedApiUrl.host))
      } catch (e) {
        console.warn("[AUTH_CONTEXT] apiHost invalid after login:", resolvedApiUrl.toString(), e)
        return { kind: "error", message: "Invalid API host" }
      }

      return { kind: "ok", url: resolvedApiUrl, usedOverride }
    },
    [localApiHost, persistedApiHost],
  )

  const markSessionDeactivated = React.useCallback(() => {
    // Terminal sponsor-gone state supersedes displacement: a WS 4001 close that
    // races the HTTP 401 must not re-surface the takeover dialog on a channel
    // that no longer exists.
    if (isSponsorInactiveRef.current) return
    isSessionDeactivatedRef.current = true
    setIsSessionDeactivated(true)
  }, [])

  // When the takeover dialog appears, abort any in-flight queries so they
  // don't surface "Failed to fetch …" errors behind the dialog (the
  // dialog is the user's remediation path; secondary error overlays add noise
  // and confusion). cancelQueries also halts retry timers and prevents
  // refetch-on-reconnect from immediately re-firing while displaced. When
  // the user picks "Resume Here" and resumeSession succeeds, the queries
  // are explicitly invalidated below so the UI refetches with the fresh
  // context token.
  //
  // Also mirrors isSessionDeactivated → isSessionDeactivatedRef so the api
  // recovery callbacks (closed over the initial-render state) can observe
  // the latest value and short-circuit silent recovery while the user is
  // making a choice on the dialog. Without this guard, a query that
  // refetches on focus and returns CONTEXT_TOKEN_INVALID would silently
  // call POST /context/refresh, which could mint a new session on this
  // displaced tab and DISPLACE THE TAB THAT IS CURRENTLY ACTIVE elsewhere.
  // The contract: while the dialog is up, the app is frozen until the user
  // picks Resume Here (which calls reactivateContext via resumeSession) or
  // Log out.
  useEffect(() => {
    isSessionDeactivatedRef.current = isSessionDeactivated
    if (isSessionDeactivated) {
      console.log("[AUTH_CONTEXT] Session deactivated — cancelling in-flight queries")
      queryClient.cancelQueries().catch((e) => {
        console.warn("[AUTH_CONTEXT] cancelQueries threw:", e)
      })
    }
  }, [isSessionDeactivated, queryClient])

  // Mirror isSponsorInactive → ref and freeze the app while the terminal dialog
  // is up: cancel in-flight queries so they don't keep hammering the dead
  // sponsor with 401s behind the dialog (same rationale as the deactivation
  // effect above).
  useEffect(() => {
    isSponsorInactiveRef.current = isSponsorInactive
    if (isSponsorInactive) {
      console.log("[AUTH_CONTEXT] Sponsor inactive — cancelling in-flight queries")
      queryClient.cancelQueries().catch((e) => {
        console.warn("[AUTH_CONTEXT] cancelQueries threw:", e)
      })
    }
  }, [isSponsorInactive, queryClient])

  /**
   * Bootstraps a session via POST /context/switch (isLogin: true).
   * Sets isRegistrationPending for the duration so coincident SESSION_DEACTIVATED
   * errors from parallel requests are suppressed during this window.
   */
  const bootstrapSessionContext = React.useCallback(
    async (
      sponsorId: string,
      role?: import("types/role").UserRole,
    ): Promise<{ kind: "ok" } | { kind: "error"; message: string }> => {
      setIsRegistrationPending(true)
      isRegistrationPendingRef.current = true

      const work = (async () => {
        try {
          const result = await api.switchContext({ sponsorId, role, isLogin: true })
          if (result.kind !== "ok") {
            return { kind: "error" as const, message: "session_bootstrap_failed" }
          }
          return { kind: "ok" as const }
        } finally {
          setIsRegistrationPending(false)
          isRegistrationPendingRef.current = false
          bootstrapPromiseRef.current = null
        }
      })()

      bootstrapPromiseRef.current = work
      return await work
    },
    [api],
  )

  /**
   * Foreground session refresh: POST /context/refresh if a token is stored,
   * no-op otherwise (cannot bootstrap without a sponsor_id).
   * Sets isRegistrationPending to suppress the foreground hook from racing.
   */
  const claimSession = React.useCallback(async () => {
    if (!getContextToken()) {
      return { kind: "ok" as const }
    }
    setIsRegistrationPending(true)
    isRegistrationPendingRef.current = true
    try {
      const result = await api.refreshContext()
      if (result.kind !== "ok") {
        return { kind: "error" as const, message: "session_refresh_failed" }
      }
      return { kind: "ok" as const }
    } finally {
      setIsRegistrationPending(false)
      isRegistrationPendingRef.current = false
    }
  }, [api])

  const notifySession = React.useCallback(async () => {
    return claimSession()
  }, [claimSession])

  const resumeSession = React.useCallback(async () => {
    // "Resume Here" — POST /context/reactivate to explicitly reclaim the displaced tab's session.
    // Accepts the current (possibly expired) context token.
    // If reactivate fails, surface the error rather than silently bootstrapping a new session
    // (we don't have a sponsor_id to bootstrap with from this path).
    const reactivateResult = await api.reactivateContext()
    const result: { kind: "ok" } | { kind: "error"; message: string } =
      reactivateResult.kind === "ok"
        ? { kind: "ok" }
        : (() => {
            console.warn(
              "[AUTH_CONTEXT] resumeSession: reactivateContext failed",
              reactivateResult.kind,
            )
            return { kind: "error" as const, message: "session_reactivate_failed" }
          })()

    if (result.kind === "error") {
      return result
    }
    isSessionDeactivatedRef.current = false
    setIsSessionDeactivated(false)
    // The competing session may have advanced or changed the conversation while this
    // client was displaced. Trigger a conversation re-sync so the UI reflects the
    // server's current state (same mechanism used on foreground and role switch).
    clearConversationStatusFlags()
    // Invalidate ALL React Query caches so any data that was fetched
    // (or paused/cancelled) during the displacement window is refetched with
    // the freshly-minted X-Context-Token. Without this, queries that bailed
    // gracefully on `kind === "unauthorized"` (returning null/empty for the
    // dialog window) would keep that empty data — Resume Here would leave
    // the UI stuck in an empty state until something else triggered a
    // refetch. Pairs with cancelQueries() above (triggered when the dialog
    // first appeared).
    queryClient.invalidateQueries().catch((e) => {
      console.warn("[AUTH_CONTEXT] invalidateQueries after resume threw:", e)
    })
    return result
  }, [api, clearConversationStatusFlags, queryClient])

  // Update the API URL from the backend and optionally persist it.
  //
  // `alsoUpdateDefault`: when true, ALSO writes the resolved host to the
  // per-user (localStorage) `defaultApiHost` slot. signIn passes true so that
  // subsequent freshly-opened tabs (with empty sessionStorage) can fall back
  // to a valid host. Sponsor-switch callers intentionally leave it false —
  // switching the active sponsor in this tab should NOT change the default
  // that other / future tabs bootstrap against.
  //
  // `skipPersist`: when true, only update `api.apisauce.setBaseURL()` in-place
  // (so the in-flight switch request targets the correct host) but do NOT call
  // setApiHost/setDefaultApiHost. This avoids triggering a React re-render that
  // would recreate the `api` useMemo instance and lose the session-switch lock —
  // which would allow queries cleared by clearApiCache() to race ahead of the
  // switchContext call before the context token is stored. Call without skipPersist
  // once the switch is complete to persist the new host.
  const updateApiHostFromBackend: AuthContextType["updateApiHostFromBackend"] = useCallback(
    (apiBaseUrl: string, options?: { alsoUpdateDefault?: boolean; skipPersist?: boolean }) => {
      console.log("[AUTH_CONTEXT] Updating API URL from backend:", apiBaseUrl)
      const apiUrlResult = resolveApiUrl(apiBaseUrl)
      if (apiUrlResult.kind === "error") {
        return { kind: "error", message: apiUrlResult.message }
      }

      console.log("[AUTH_CONTEXT] API URL resolved:", apiUrlResult.url.toString())

      if (!apiUrlResult.usedOverride) {
        const updatedApiHost = apiUrlResult.url.host
        // Always update the current api instance's base URL immediately so the
        // in-flight request targets the correct host right away.
        api.apisauce.setBaseURL(toBaseURL(updatedApiHost))
        if (!options?.skipPersist) {
          console.log("[AUTH_CONTEXT] API host updated and stored:", updatedApiHost)
          setApiHost(updatedApiHost)
          if (options?.alsoUpdateDefault) {
            setDefaultApiHost(updatedApiHost)
          }
        }
      }

      return { kind: "ok" }
    },
    [api.apisauce, resolveApiUrl, setApiHost, setDefaultApiHost],
  )

  const signIn: AuthContextType["signIn"] = async ({ email, password }) => {
    // FIXME: this is defensive, we don't really know why sometimes it's not sufficient to
    // run this only on logout, but we have user reports about it
    clearAllTemporaryData()
    // Clear any lingering terminal state from a previous (deleted-channel) session.
    isSponsorInactiveRef.current = false
    setIsSponsorInactive(false)

    // Special case: App review account always uses staging auth server
    // This allows app review to test against staging backend before production deployment
    const authApiToUse =
      email.toLowerCase() === APP_REVIEW_EMAIL.toLowerCase()
        ? new AuthApi({
            config: {
              url: toBaseURL(APP_REVIEW_AUTH_HOST),
              timeout: 10000,
            },
          })
        : authApi

    const response = await authApiToUse.login({ username: email, password })

    if ((response as GeneralApiProblem).data?.code === "NEW_PASSWORD_REQUIRED") {
      return { kind: "error", message: "NEW_PASSWORD_REQUIRED" }
    }

    if (response.kind !== "ok") {
      return {
        kind: "error",
        message: (response as GeneralApiProblem).data?.message || "Error signing in",
      }
    }

    // Race guard: mark signIn as in-progress BEFORE any state update that
    // flips isAuthenticated. The foreground claim hook in AuthExclusiveSessionEffects
    // observes this on its first render with isAuthenticated=true and skips its
    // own bootstrap notifyLogin — otherwise it would race against claimSession()
    // below, mint a competing session_id, and deactivate the session signIn just
    // established (causing 401 SESSION_DEACTIVATED on activity-user-status,
    // conversations/current, etc. and surfacing the "Another session started" dialog).
    isSigningInRef.current = true
    setIsSigningIn(true)

    try {
      const loginMeta = response.loginMeta
      if (!loginMeta) {
        return { kind: "error", message: "No login meta available" }
      }

      const apiBaseUrl = loginMeta.targetUrls?.webServerUrl
      if (!apiBaseUrl) {
        return { kind: "error", message: "No API URL available" }
      }

      console.log("[AUTH_CONTEXT] Login success", loginMeta)

      // Update the API host. Pass `alsoUpdateDefault: true` so the resolved
      // host is ALSO mirrored to the per-user (localStorage) `defaultApiHost`
      // slot. That gives a freshly-opened tab — which inherits the user's
      // Cognito tokens from localStorage but starts with empty sessionStorage
      // — a valid host to bootstrap against (so it can call `getSponsorList`
      // and reach `/select-sponsor` instead of hanging on
      // `"unknown-api-host"`). Sponsor-switch callers do NOT pass this
      // flag — see the comment on `updateApiHostFromBackend` above.
      const updateApiUrlResult = updateApiHostFromBackend(apiBaseUrl, {
        alsoUpdateDefault: true,
      })
      if (updateApiUrlResult.kind === "error") {
        return { kind: "error", message: updateApiUrlResult.message }
      }

      const sponsorId = loginMeta.sponsorId

      // Store our auth tokens
      updateAuthTokens(response.result)
      setIsLoggingOut(false)

      // Check the backend API version before any other authenticated request.
      // updateAuthTokens() has already populated authTokensRef so getHeaders()
      // will use the fresh Cognito tokens; no context token is needed for this
      // endpoint. Bailing here prevents bootstrapSessionContext / getRoleInfo / etc.
      // from firing against an incompatible backend (which would 401 and trip
      // auto-logout before the user can see the outdated screen). The tokens are
      // intentionally kept so the caller can navigate into (app) and let the
      // layout's version gate redirect to /outdated.
      const versionResult = await api.getBackendAPIVersion()
      if (versionResult.kind === "ok" && versionResult.version !== Config.BACKEND_API_VERSION) {
        logSentryBreadcrumb(
          `version_mismatch_on_login:expected_${Config.BACKEND_API_VERSION}:got_${versionResult.version}`,
        )
        // Seed the cache so (a) the layout's useBackendAPIVersion query in the
        // gate uses this result without a redundant fetch, and (b) the
        // handleAuthFailure suppression that consults this same key can kick in
        // immediately if a late 401 lands between here and the gate render.
        queryClient.setQueryData(["backendAPIVersion"], versionResult.version)
        return { kind: "error", message: "VERSION_MISMATCH" }
      }

      // Hold the sponsor list in-memory so SelectSponsor can render without a network call.
      const sponsorsList = loginMeta.sponsors.length > 0 ? loginMeta.sponsors : null
      pendingLoginSponsorsRef.current = sponsorsList
      setPendingLoginSponsors(sponsorsList)

      if (loginMeta.requiresSponsorSelection) {
        // Multi-sponsor: SelectSponsor will bootstrap via api.switchContext({isLogin:true}).
        // Do NOT call setSponsorId here — loginMeta.sponsorId is set to the first
        // sponsor's id as a hint, but the user hasn't actually picked yet. Setting
        // it would cause SelectSponsor.handleSwitchSponsorPressed to short-circuit
        // when the user clicks the (already-matching) first sponsor, skipping
        // switchContext entirely and navigating with no context token — every
        // subsequent request 401s.
        // Also clear any stale context token from a previous session so no requests
        // fire with it while the user is picking a sponsor (which would return
        // SESSION_DEACTIVATED and show the takeover dialog prematurely).
        clearContextToken()
        return { kind: "ok", requiresSponsorSelection: true }
      }

      // Single-sponsor: bootstrap immediately.
      if (!sponsorId) {
        return { kind: "error", message: "No sponsor ID available for bootstrap" }
      }
      setSponsorId(sponsorId)
      const defaultRole = getLoginRole(loginMeta.sponsors[0]?.default_role)
      const bootstrapResult = await bootstrapSessionContext(sponsorId, defaultRole)
      if (bootstrapResult.kind !== "ok") {
        return { kind: "error", message: "Error establishing session after login" }
      }

      console.log("[AUTH_CONTEXT] bootstrapSessionContext (login) success")
      logSentryBreadcrumb(`exclusive_session_claim_success:${sponsorId}`)
      pendingLoginSponsorsRef.current = null
      setPendingLoginSponsors(null)

      // Query the server for the complete state after login to establish our intended session
      const roleInfoResponse = await api.getRoleInfo()
      if (roleInfoResponse.kind === "ok") {
        setUserSelectedSession({
          sponsorId: sponsorId || roleInfoResponse.result.main_user_id.split(":")[0],
          roleName: roleInfoResponse.result.current_role,
          userId: roleInfoResponse.result.current_user_id,
        })
        api.reportActivity(Date.now(), "login", "login").catch((error) => {
          console.warn("[AUTH_CONTEXT] Failed to report login activity:", error)
        })
      } else {
        console.error(
          `[AUTH_CONTEXT] Failed to verify session after login: getRoleInfo returned ${roleInfoResponse.kind}`,
        )
        return {
          kind: "error",
          message: `Login verification failed: ${roleInfoResponse.kind}`,
        }
      }

      return { kind: "ok", requiresSponsorSelection: false }
    } finally {
      isSigningInRef.current = false
      setIsSigningIn(false)
    }
  }

  const changePassword: AuthContextType["changePassword"] = async ({
    email,
    oldPassword,
    newPassword,
  }) => {
    // Check if user is currently authenticated and has session state
    const wasAuthenticated = !!authTokens?.AccessToken
    const previousSession = wasAuthenticated ? getUserSelectedSession() : null
    const hadValidSession = wasAuthenticated && previousSession

    // Special case: App review account always uses staging auth server
    const authApiToUse =
      email.toLowerCase() === APP_REVIEW_EMAIL.toLowerCase()
        ? new AuthApi({
            config: {
              url: toBaseURL(APP_REVIEW_AUTH_HOST),
              timeout: 10000,
            },
          })
        : authApi

    const response = await authApiToUse.changePassword({
      email,
      oldPassword,
      newPassword,
    })

    if (response.kind === "ok") {
      console.log(
        "[AUTH_CONTEXT] Password changed for authenticated user, refreshing tokens without resetting session",
      )

      // If the user was authenticated before the password change, we need to login to refresh the tokens.
      // If the user was unauthenticated (as in the forced passsword update flow),
      // we do not login here. The login happens in the update-password component
      // after the password change.
      // FIXME: We should probably unify the login responsibitily here rather than split with
      // the update-password component.
      if (hadValidSession) {
        // After password change, we need to get new tokens by logging in with the new password.
        const loginResponse = await authApiToUse.login({
          username: email,
          password: newPassword,
        })

        if (loginResponse.kind === "ok") {
          // Update tokens but DON'T call resetSponsor - preserve existing session.
          // FIXME: We should stop relying on the current `permission_cookie` behavior when
          // changing passwords. Although we have a new login, our `permission_cookie` which
          // contains our role authorization isn't invalidated. Our local session state (userSelectedSession)
          // is also not reset, so implicitly the rest of our existing session remains valid following
          // the password change so no further session management is needed. This seems like it's probably
          // a bug in the backend and eventually we'll need to restore the `permission_cookie` here as well.
          updateAuthTokens(loginResponse.result)
          console.log("[AUTH_CONTEXT] Tokens refreshed after password change")
        } else {
          // Login failed after password change - this shouldn't happen but handle it
          console.error("[AUTH_CONTEXT] Failed to login after password change:", loginResponse.kind)
          return {
            kind: "error",
            message: "Password changed but failed to refresh session. Please log in again.",
          }
        }
      }

      return { kind: "ok" }
    } else {
      // Extract meaningful error messages based on the response type
      let errorMessage = "Error changing password"

      if (response.data?.message) {
        errorMessage = response.data.message
      } else if (response.data?.detail) {
        errorMessage = response.data.detail
      } else {
        switch (response.kind) {
          case "server":
            errorMessage = "Server error occurred. Please try again later."
            break
          case "unauthorized":
            errorMessage = "Your old password is incorrect. Please try again."
            break
          case "cannot-connect":
            errorMessage = "Connection error. Please check your internet connection."
            break
          case "timeout":
            errorMessage = "Request timed out. Please try again."
            break
          case "forbidden":
            errorMessage = "You don't have permission to change your password."
            break
          default:
            errorMessage = "Error changing password. Please try again."
        }
      }

      return { kind: "error", message: errorMessage }
    }
  }

  const logout = async () => {
    if (authTokens?.AccessToken && authTokens?.RefreshToken) {
      try {
        // Send the server logout request FIRST (still carries
        // X-Context-Token so the backend can deactivate the right session),
        // then clear the stored token afterwards. Reversing the order would
        // strip the header from the logout request and risk a backend rejection.
        await api.logout(authTokens.AccessToken, authTokens.RefreshToken)
        console.log("logout: logout success")
      } catch (error) {
        console.error("logout: error logging out:", error)
      }
      // no need to handle errors, as we're logging out anyway
    } else {
      // noop - no tokens in state, so user is already unauthenticated
      console.log("logout: no tokens in state, so user is already unauthenticated")
    }
    clearContextToken()
  }

  // All the data about the user
  const clearAllUserData = useCallback(() => {
    clearUserCache()
    clearCachedConversations()
  }, [clearUserCache, clearCachedConversations])

  // All the data about sponsors
  const clearSponsorData = useCallback(() => {
    setSponsorId(undefined)
  }, [setSponsorId])

  // All the data about a logged in session
  const clearLoginData = useCallback(() => {
    updateAuthTokens(undefined)
    clearLastShownSplashSponsorId()
  }, [updateAuthTokens])

  // All the data about the API configuration
  const clearApiData = useCallback(() => {
    setApiHost(undefined)
    // Clear the per-user default-host fallback too. Once the user logs out,
    // the next login may be a different account whose default sponsor lives
    // on a different env (dev1 vs dev2 vs staging) — we don't want a stale
    // value from the previous account misrouting the new tab's bootstrap
    // calls.
    setDefaultApiHost(undefined)
    clearApiCache()
  }, [clearApiCache, setApiHost, setDefaultApiHost])

  // Clear the TanStack Query cache
  const clearQueryCache = useCallback(() => {
    queryClient.clear()
  }, [queryClient])

  // All (most) in-memory cache and storage data that shouldn't persist across application logins
  // This omits global (and mutually exclusive) status flags e.g. the logout, resetDemo, and clearAllStorage flags
  // so that code that clears status flags avoids clearing the global mutations early.
  const clearAllTemporaryData = useCallback(() => {
    console.log("[AUTH_CONTEXT] Clearing all temporary data")
    clearQueryCache()
    clearAllUserData()
    clearSponsorData()
    clearRoleData()
    clearApiData()
    clearLoginData()
    clearConfigCache()
    clearConversationStatusFlags()
    clearVideoThumbnailCache()
    pendingLoginSponsorsRef.current = null
    setPendingLoginSponsors(null)
    console.log("[AUTH_CONTEXT] All temporary data cleared")
  }, [
    clearQueryCache,
    clearAllUserData,
    clearSponsorData,
    clearRoleData,
    clearApiData,
    clearLoginData,
    clearConfigCache,
    clearConversationStatusFlags,
  ])

  const localLogoutAfterDeactivation = React.useCallback(() => {
    setIsLoggingOut(true)
    try {
      // Skip the server POST /logout — the session is already inactive on the
      // server. Clear local state including the stored context token.
      clearContextToken()
      clearAllTemporaryData()
      isSessionDeactivatedRef.current = false
      isSponsorInactiveRef.current = false
    } finally {
      setIsSessionDeactivated(false)
      setIsSponsorInactive(false)
      setIsLoggingOut(false)
    }
  }, [clearAllTemporaryData, setIsLoggingOut])

  // "Choose another channel" path for the terminal sponsor-inactive state.
  // The sponsor is gone but the user's Cognito auth is still valid, so we keep
  // the auth tokens (and the per-user defaultApiHost the channel picker needs)
  // and only clear this tab's per-channel state: context token, sponsor
  // selection, role, and conversation/config caches. Clearing the sponsor id
  // makes the authenticated layout redirect to /setup/select-sponsor.
  const recoverFromSponsorInactive = React.useCallback(() => {
    clearContextToken()
    clearSponsorData()
    clearAllUserData()
    clearRoleData()
    clearConfigCache()
    clearConversationStatusFlags()
    isSessionDeactivatedRef.current = false
    isSponsorInactiveRef.current = false
    setIsSessionDeactivated(false)
    setIsSponsorInactive(false)
  }, [
    clearSponsorData,
    clearAllUserData,
    clearRoleData,
    clearConfigCache,
    clearConversationStatusFlags,
  ])

  // Clear all persisted storage
  const clearAllStorageData = useCallback(async () => {
    console.log("[AUTH_CONTEXT] Clearing all storage data")
    // Clear all MMKV storage (includes auth tokens, API hosts, sponsor data, etc.)
    clearAllMmkvStorage()
    // On web, the context token lives in sessionStorage (outside MMKV), so we clear
    // it explicitly. On native, MMKV is the backing store and the call above already
    // covered it, but clearContextToken is idempotent.
    clearContextToken()
    console.log("[AUTH_CONTEXT] All storage data cleared")
  }, [])

  // If the auth tokens disappear outside of an intentional logout, clear the query cache.
  // Intentional logout already clears via clearAllTemporaryData(); skipping here avoids
  // a second clear that races with post-logout channel-info seeding on the login screen.
  React.useEffect(() => {
    if (!authTokens?.AccessToken) {
      if (getLogoutStatus()) return
      console.log("[AUTH_CONTEXT] Clearing query cache due to authTokens change")
      clearQueryCache()
    }
  }, [authTokens?.AccessToken, clearQueryCache])

  const getUserEmail = useCallback(() => {
    if (!authTokens?.IdToken) return undefined

    try {
      const decoded = jwtDecode(authTokens.IdToken) as { email?: string }
      return decoded.email
    } catch (error) {
      console.error("Error decoding JWT token:", error)
      return undefined
    }
  }, [authTokens?.IdToken])

  const switchSponsor = useCallback(
    async (
      sponsorId: string,
      opts?: { isLogin?: boolean; role?: import("types/role").UserRole },
    ) => {
      logSentryBreadcrumb(`sponsor_switch_initiated:${sponsorId}`)

      // Clear all previous data that depends on the sponsor
      clearSponsorData()
      clearAllUserData()
      clearRoleData()
      clearApiCache()
      clearConfigCache()
      clearConversationStatusFlags()

      // POST /context/switch. isLogin:true for login bootstrap (no context token present);
      // isLogin:false for in-app sponsor switches (context token already stored).
      // The lock around switchContext is released BEFORE setting React state below
      // so that:
      //   * concurrent requests (sponsor-list, current-conversation, etc.) hold in
      //     the request interceptor until the new context_token is stored.
      //   * the follow-up getRoleInfo call (and other React-Query refetches that
      //     react to the state updates below) do NOT deadlock waiting on a lock
      //     that this same call stack would otherwise hold.
      const isLogin = opts?.isLogin ?? false
      api.beginSessionSwitch()
      let switchResult
      try {
        switchResult = await api.switchContext({ sponsorId, role: opts?.role, isLogin })
      } finally {
        api.completeSessionSwitch()
      }

      if (switchResult.kind !== "ok") {
        logSentryBreadcrumb(`sponsor_switch_failed:${sponsorId}:${switchResult.kind}`)
        throw new Error(`context_switch_failed:${switchResult.kind}`)
      }

      // Update the sponsor ID and clear the post-login sponsor list NOW that the
      // context token is stored. Re-renders triggered by these setters will cause
      // gated React-Query hooks (e.g. SelectSponsor.useSponsorListQuery,
      // useCurrentConversationQuery) to fire — they'll find the fresh token and
      // succeed instead of 401-ing.
      setSponsorId(sponsorId)
      pendingLoginSponsorsRef.current = null
      setPendingLoginSponsors(null)

      // Fetch role info for the just-switched sponsor. Uses the fresh
      // context_token so it returns the new sponsor's role.
      const roleInfoResponse = await api.getRoleInfo()
      if (roleInfoResponse.kind === "ok") {
        setUserSelectedSession({
          sponsorId,
          roleName: roleInfoResponse.result.current_role,
          userId: roleInfoResponse.result.current_user_id,
        })
        logSentryBreadcrumb(`sponsor_switch_success:${sponsorId}`)
        api.reportActivity(Date.now(), "channelChange", "channelChange").catch((error) => {
          console.warn("[AUTH_CONTEXT] Failed to report channelChange activity:", error)
        })
      } else {
        // If we can't verify the sponsor switch, log error but don't throw
        // The user has already switched UI state, so we'll let drift detection handle recovery
        console.error(
          `[AUTH_CONTEXT] Failed to verify sponsor switch: getRoleInfo returned ${roleInfoResponse.kind}`,
        )
      }
    },
    [
      clearSponsorData,
      clearAllUserData,
      clearRoleData,
      clearApiCache,
      clearConfigCache,
      clearConversationStatusFlags,
      setSponsorId,
      api,
    ],
  )

  const value = {
    needsRefresh,
    isRefreshing,
    isAuthenticated: !isLoggingOut && !!authTokens?.AccessToken,
    initialAuthStateLoaded,
    signIn,
    logout,
    changePassword,
    clearApiData,
    clearApiCache,
    clearSponsorData,
    clearAllUserData,
    clearAllTemporaryData,
    clearAllStorageData,
    api,
    authApi,
    getAuthToken,
    getUserEmail,
    apiHost,
    setApiHost,
    updateApiHostFromBackend,
    authHost,
    setAuthHost,
    sponsorId,
    switchSponsor,
    pendingLoginSponsors,
    setPendingLoginSponsors,
    isSessionDeactivated,
    isSponsorInactive,
    recoverFromSponsorInactive,
    isRegistrationPending,
    isSigningIn,
    claimSession,
    notifySession,
    resumeSession,
    markSessionDeactivated,
    localLogoutAfterDeactivation,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AuthExclusiveSessionEffects />
    </AuthContext.Provider>
  )
}

function AuthExclusiveSessionEffects() {
  const {
    initialAuthStateLoaded,
    isAuthenticated,
    isSessionDeactivated,
    isRegistrationPending,
    isSigningIn,
    notifySession,
  } = useAuth()
  // Use notifySession (POST /context/refresh only) instead of the full claimSession
  // (which resets role + sponsor). We don't want to reset the user's current context
  // just because they brought the app back to the foreground.
  //
  // Race guard — defense in depth, two complementary safeguards:
  //   1. `isAuthenticated && !isRegistrationPending` masks the hook's view of
  //      isAuthenticated while a login bootstrap (signIn's bootstrapSessionContext or a
  //      sponsor switch) is pending. Without this, the mount-claim path would
  //      fire concurrently with the in-flight bootstrap and mint a
  //      competing session_id.
  //   2. `isBootstrappingSession = isSigningIn || isRegistrationPending`
  //      keeps the suppression active for the entire signIn flow — including
  //      the post-claimSession steps (getRoleInfo, reportActivity) where
  //      isRegistrationPending has already cleared but the bootstrap is not
  //      yet complete. The hook also sets hasMountClaimedRef permanently
  //      under this flag, so no spurious re-fire happens after signIn ends.
  const isBootstrappingSession = isSigningIn || isRegistrationPending
  useClaimSessionOnForeground({
    initialAuthStateLoaded,
    isAuthenticated: isAuthenticated && !isRegistrationPending,
    isSessionDeactivated,
    isBootstrappingSession,
    claimSession: notifySession,
  })
  return (
    <>
      <SessionTakeoverDialog />
      <SponsorInactiveDialog />
    </>
  )
}

export const useAuth = () => {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
